import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import SimpleModal from "./SimpleModal";
import PasswordInput from "./PasswordInput";
import { useToast } from "../ToastProvider";
import { router } from "../../data/Services/ordersRouter";
import { useAuthStore } from "../../store/authStore";

function ChangePasswordModal({ visible, onHide }) {
  const { showError, showSuccess } = useToast();
  const { getUsername, getBroker } = useAuthStore();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    noSpaces: false,
    lowerCase: false,
    upperCase: false,
    number: false,
    symbol: false,
    notInLastSix: false,
  });

  // Clear form when modal is closed
  useEffect(() => {
    if (!visible) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordRequirements({
        minLength: false,
        noSpaces: false,
        lowerCase: false,
        upperCase: false,
        number: false,
        symbol: false,
        notInLastSix: false,
      });
    }
  }, [visible]);

  const validatePassword = (password) => {
    const requirements = {
      minLength: password.length >= 6,
      noSpaces: !password.startsWith(" ") && !password.endsWith(" "),
      lowerCase: /[a-z]/.test(password),
      upperCase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      // Match server's exact symbol set: ~`!@#$%^&*()-_+=\|]}[{'/?.>,<
      symbol: /[~`!@#$%^&*()\-_+=\\|}\][{'"/?.>,<]/.test(password),
      notInLastSix: true, // Simulação, ajuste conforme necessário
    };
    setPasswordRequirements(requirements);
    return Object.values(requirements).every(Boolean);
  };

  const handleSubmit = async () => {
    if (!currentPassword.trim()) {
      showError("Por favor, digite sua senha atual", "Campo obrigatório");
      return;
    }

    if (!validatePassword(newPassword)) {
      showError(
        "Os requisitos de senha não foram atingidos.",
        "Erro de Validação",
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      showError("As senhas não coincidem.", "Erro de Validação");
      return;
    }

    const username = getUsername();
    const broker = getBroker();

    if (!username) {
      showError(
        "Usuário não encontrado. Faça login no MarketData primeiro.",
        "Erro de autenticação",
      );
      return;
    }

    if (!broker) {
      showError(
        "Corretora não encontrada. Faça login no MarketData primeiro.",
        "Erro de autenticação",
      );
      return;
    }

    setIsLoading(true);

    try {
      const result = await router.changePwd(
        broker,
        username,
        currentPassword,
        newPassword,
      );

      if (result === "OK") {
        showSuccess("Senha alterada com sucesso!", "Sucesso");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        if (onHide) onHide();
      } else if (result === "FAIL") {
        // Keep modal open for retry on generic failure
        showError(
          "Por favor, entre em contato com nossa central de suporte!",
          "Erro",
        );
      } else if (result && result.startsWith("FAIL:")) {
        // Keep modal open for retry on specific failure
        const errorMessage = result.substring(5);
        showError(errorMessage, "Erro");
      } else if (result) {
        // Keep modal open for retry on unknown failure
        showError(result, "Erro");
      } else {
        // Keep modal open for retry on unknown error
        showError("Erro ao alterar senha. Tente novamente.", "Erro");
      }
    } catch (error) {
      // Keep modal open for retry on exception
      console.error("Change password error:", error);
      showError(error.message || "Erro inesperado ao alterar senha", "Erro");
    } finally {
      setIsLoading(false);
    }
  };

  const currentBroker = getBroker();

  // Broker names mapping
  const getBrokerName = (brokerCode) => {
    const brokerNames = {
      XPI: "XP Investimentos",
      CLEAR: "Clear Corretora",
      TZ: "TradeZone",
      RICO: "Rico Investimentos",
      SIMULATOR: "Simulador",
    };
    return brokerNames[brokerCode] || brokerCode;
  };

  const showMatchStatus = confirmPassword && newPassword;
  const passwordsMatch = confirmPassword === newPassword;

  return (
    <SimpleModal
      title={`Alterar Senha de Roteamento (${getBrokerName(currentBroker) || "N/A"})`}
      visible={visible}
      onHide={onHide}
      onConfirm={handleSubmit}
      confirmLabel={isLoading ? "Alterando..." : "Alterar"}
      showCancel={!isLoading}
      size="medium"
      variant="form"
      className="change-password-modal"
    >
      <div className="change-password-form">
        {/* Requirements */}
        <div className="requirements-box">
          <strong>Requisitos de Senha:</strong>
          <ul>
            <li className={passwordRequirements.minLength ? "valid" : ""}>
              Mínimo de 6 caracteres
            </li>
            <li className={passwordRequirements.noSpaces ? "valid" : ""}>
              Não pode começar ou terminar com espaço
            </li>
            <li className={passwordRequirements.lowerCase ? "valid" : ""}>
              Pelo menos uma letra minúscula
            </li>
            <li className={passwordRequirements.upperCase ? "valid" : ""}>
              Pelo menos uma letra maiúscula
            </li>
            <li className={passwordRequirements.number ? "valid" : ""}>
              Pelo menos um número
            </li>
            <li className={passwordRequirements.symbol ? "valid" : ""}>
              Pelo menos um símbolo (~`!@#$%^&*...)
            </li>
            <li className={passwordRequirements.notInLastSix ? "valid" : ""}>
              Não pode estar entre as 6 últimas senhas
            </li>
          </ul>
        </div>

        {/* Current password */}
        <div className="form-group">
          <label htmlFor="currentPassword">Senha Atual</label>
          <PasswordInput
            inputId="currentPassword"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={isLoading}
          />
        </div>

        {/* New password */}
        <div className="form-group">
          <label htmlFor="newPassword">Nova Senha</label>
          <PasswordInput
            inputId="newPassword"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              validatePassword(e.target.value);
            }}
            disabled={isLoading}
            data-evo-id="changepasswordmodal-input-new-password"
          />
        </div>

        {/* Confirm password */}
        <div className="form-group">
          <label htmlFor="confirmPassword">Confirmação</label>
          <PasswordInput
            inputId="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isLoading}
          />
          {showMatchStatus && (
            <span
              className={`match-status ${passwordsMatch ? "match" : "no-match"}`}
            >
              {passwordsMatch ? "✓ Senhas coincidem" : "✗ Senhas não coincidem"}
            </span>
          )}
        </div>
      </div>
    </SimpleModal>
  );
}

ChangePasswordModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default ChangePasswordModal;
